import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Sidebar } from './Sidebar';
import type { UserTier } from '../types';

interface LayoutProps {
  children: React.ReactNode;
  mode?: 'full' | 'sidebar';
  usageRemaining?: number | null;
  tier?: UserTier;
}

export function Layout({
  children,
  mode = 'full',
  usageRemaining = null,
  tier,
}: LayoutProps) {
  const { email, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  if (mode === 'sidebar') {
    return (
      <div
        style={{
          height: '100dvh',
          overflow: 'hidden',
          background: 'var(--bg-primary)',
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
      >
        <Sidebar
          currentPath={location.pathname}
          email={email}
          usageRemaining={usageRemaining}
          tier={tier}
          onSignOut={handleLogout}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
        />

        <div className="sidebar-content-wrapper"
          style={{
            marginLeft: '220px',
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Mobile top bar — hamburger only, sidebar handles nav */}
          <div className="sidebar-mobile-topbar"
            style={{
              display: 'none',
              flexShrink: 0,
              background: 'var(--nav-bg)',
              borderBottom: '1px solid var(--nav-border)',
              padding: '10px 16px',
              backdropFilter: 'blur(12px)',
            }}
          >
            <button
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              style={{
                background: 'none',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                padding: '8px 12px',
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                fontSize: '16px',
              }}
            >
              ☰
            </button>
          </div>

          <main className="sidebar-main"
            style={{
              flex: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {children}
          </main>
        </div>

        <style>{`
          @media (max-width: 768px) {
            .sidebar-mobile-topbar { display: block !important; }
            .sidebar-content-wrapper { margin-left: 0 !important; }
          }
        `}</style>
      </div>
    );
  }

  // ── Existing 'full' mode below — unchanged ──────────────────────────────
  const navItems = [
    { to: '/editor', label: 'Editor' },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      fontFamily: "'Inter', system-ui, sans-serif",
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Top nav */}
      <nav style={{
        background: 'var(--nav-bg)',
        borderBottom: '1px solid var(--nav-border)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backdropFilter: 'blur(12px)',
      }}>
        <div style={{
          maxWidth: '1100px',
          margin: '0 auto',
          padding: '0 24px',
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          {/* Left: logo */}
          <Link to="/dashboard" style={{ textDecoration: 'none' }}>
            <span style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: '18px',
              fontWeight: 700,
              letterSpacing: '-0.02em',
            }}>
              <span style={{ color: 'var(--accent)' }}>My</span>
              <span style={{ color: 'var(--text-primary)' }}>IQ</span>
            </span>
          </Link>

          {/* Desktop right nav */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
          }} className="desktop-nav">
            {navItems.map(item => (
              <Link
                key={item.to}
                to={item.to}
                style={{
                  padding: '6px 14px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  textDecoration: 'none',
                  color: location.pathname === item.to
                    ? 'var(--accent)' : 'var(--text-secondary)',
                  background: location.pathname === item.to
                    ? 'var(--accent-bg)' : 'transparent',
                  transition: 'all 0.2s',
                }}
              >
                {item.label}
              </Link>
            ))}
            {email && (
              <div style={{
                fontSize: '12px',
                background: 'var(--accent-bg)',
                color: 'var(--accent)',
                padding: '4px 12px',
                borderRadius: '20px',
                border: '1px solid rgba(0,212,255,0.2)',
                maxWidth: '160px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {email}
              </div>
            )}
            <button
              onClick={handleLogout}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                fontSize: '13px',
                color: 'var(--text-muted)',
                background: 'none',
                border: '1px solid var(--border)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.color = 'var(--text-primary)';
                e.currentTarget.style.borderColor = 'rgba(0,212,255,0.3)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = 'var(--text-muted)';
                e.currentTarget.style.borderColor = 'var(--border)';
              }}
            >
              Sign out
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="mobile-menu-btn"
            style={{
              display: 'none',
              background: 'none',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
            }}
          >
            {mobileOpen ? '✕' : '☰'}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div style={{
            borderTop: '1px solid var(--border)',
            background: 'var(--nav-bg)',
            padding: '12px 24px 16px',
          }} className="mobile-menu">
            {navItems.map(item => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                style={{
                  display: 'block',
                  padding: '12px 0',
                  fontSize: '15px',
                  fontWeight: 500,
                  textDecoration: 'none',
                  color: location.pathname === item.to
                    ? 'var(--accent)' : 'var(--text-secondary)',
                  borderBottom: '1px solid var(--nav-border)',
                }}
              >
                {item.label}
              </Link>
            ))}
            {email && (
              <div style={{
                padding: '12px 0',
                fontSize: '13px',
                color: 'var(--text-muted)',
                borderBottom: '1px solid var(--nav-border)',
              }}>
                {email}
              </div>
            )}
            <button
              onClick={() => { handleLogout(); setMobileOpen(false); }}
              style={{
                marginTop: '12px',
                padding: '10px 0',
                fontSize: '14px',
                color: 'var(--text-muted)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </nav>

      {/* Add CSS for responsive nav */}
      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: block !important; }
        }
        @media (min-width: 769px) {
          .mobile-menu { display: none !important; }
        }
      `}</style>

      {/* Page content */}
      <main style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '40px 24px',
        width: '100%',
        flex: 1,
      }}>
        {children}
      </main>

      {/* Footer */}
      <footer style={{
        borderTop: '1px solid var(--nav-border)',
        padding: '24px',
        textAlign: 'center',
        marginTop: '40px',
        position: 'relative',
      }}>
        <div style={{
          position: 'absolute',
          top: 0, left: '10%', right: '10%',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(0,212,255,0.4), transparent)',
        }} />
        <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
          <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
            <span style={{ color: 'var(--accent)' }}>My</span>IQ
          </span>
          {' · '}
          <a
            href="https://bennwokoye.com"
            style={{ color: 'var(--text-muted)', textDecoration: 'none' }}
          >
            bennwokoye.com
          </a>
          {' · '}
          <span>© 2026</span>
        </p>
      </footer>
    </div>
  );
}
